import { Fragment, useEffect, useState, useMemo } from "react"
import HeaderLogo from "../../sharedComponents/Header/HeaderLogo"
import { Row, Col, Container, Label, InputGroup, InputGroupText, Input, Button } from "reactstrap";
import Select, { createFilter } from "react-select";
import AutoSizer from "react-virtualized-auto-sizer";
import { Table, Column } from "react-virtualized";
import { selectStyles } from "../../config/constants";
import CustomOption from '../../sharedComponents/Helper/CustomOption';
import CustomMenuList from '../../sharedComponents/Helper/CustomMenuList';
import useAdminLogin from "../AdminLogin/useAdminLogin";
import { BsSearch, BsCaretDownFill } from "react-icons/bs";
import { Theme } from "../../config/constants";
import useUsersRoles from "../../AppMain/Settings/UsersRoles/useUsersRoles";
import EditUserSlideDrawer from "../../sharedComponents/SlideDrawer/EditUserSlideDrawer";
import { slideDrawer } from "../../reducers/UIReducer/UIActions";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import LocalStorage from "../../sharedComponents/LocalStorage/LocalStorage";
import TooltipMenu from "../../sharedComponents/TooltipMenu/TooltipMenu";
import authApi from "../../api/authApi";


const UserAdministration = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const logo = localStorage.getItem('branding') === "ARROWSTREAM" ? './arrowstreamLogoWhite.png' : process.env.REACT_APP_COMPANY_LOGO;
    const adminLoginData = useAdminLogin();
    const mfrSelectProps = adminLoginData.mfrSelectProps;
    const userRol = useSelector(({ user }) => user.userRol);
    const userPropsFromRedux = useMemo(() => (userRol ?? []).map(u => ({ label: `${u.first_name} ${u.last_name}`, value: u })), [userRol]);
    const userPropsFromAdmin = adminLoginData.userSelectProps.options ?? [];
    const userProps = userPropsFromRedux.length ? userPropsFromRedux : userPropsFromAdmin;
    const usersRolesApi = useUsersRoles.useApi();
    const [userList, setUserList] = useState(userProps ?? []);
    const [selectedUser, setSelectedUser] = useState([]);
    const [selectedMfr, setSelectedMfr] = useState(null);
    const [openMenuIndex, setOpenMenuIndex] = useState(null);

    const filterUsers = (searchPhrase) => {
        const phrase = searchPhrase.target.value.toLowerCase();
        const filteredUsers = (userProps ?? []).filter(user => {
            const val = user.value || {};
            return (
                (val.first_name || "").toLowerCase().includes(phrase) ||
                (val.last_name || "").toLowerCase().includes(phrase) ||
                (val.email || "").toLowerCase().includes(phrase) ||
                (val.title || "").toLowerCase().includes(phrase)
            );
        });
        setUserList(filteredUsers);
    }

    const exportUsers = () => {
        if (!userProps || userProps.length === 0) {
            toast.warn("No users available to export.");
            return;
        }

        const data = userProps.map(user => ({
            Name: `${user.value.first_name} ${user.value.last_name}`,
            Email: user.value.email,
            "Created Date": new Date(user.value.createdAt).toLocaleString(),
            "Last Login Date": new Date(user.value.lastLoggedIn).toLocaleString(),
            Role: user.value.title,
        }));

        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
        XLSX.writeFile(workbook, "BEPUsers.xlsx");
    }

    const inviteUser = () => {
        usersRolesApi.userInvite(false, mfrSelectProps.value.value.platform_id);
    }

    const editUser = (user) => {
        setSelectedUser(user);
        dispatch(slideDrawer.addEditUser());
    }

    const login = (e, rowData) => {
        adminLoginData.userSelectProps.onChange(rowData);
        adminLoginData.onLogin(e, rowData);
    }

    const sendInviteLink = (user) => {
        const params = {
            email: user.email,
            siteUrl: window.location.origin
        }
        toast.info(`Sending invite link to ${user.email}...`);
        authApi.resendInviteEmail.post(params)
        .then(() => {
            toast.success(`Invite link sent to ${user.email}`);
        })
        .catch(() => {
            toast.error(`Failed to send invite link to ${user.email}`);
        });
    }

    const toggleMenu = (index) => {
        setOpenMenuIndex(openMenuIndex === index ? null : index);
    }

    const { getUsers } = usersRolesApi;

    useEffect(() => {
        if (mfrSelectProps.value) {
            setSelectedMfr(mfrSelectProps.value);
            try {
                LocalStorage.manufacturerData.set(mfrSelectProps.value.value);
            } catch {}
            getUsers();
        }
    }, [mfrSelectProps.value, getUsers]);

    useEffect(() => {
        setUserList(userProps);
    }, [userProps]);

    return (
        <Fragment>
            <HeaderLogo logo={logo} />
            <Container fluid className='flex-grow-1'>
                <Row className='justify-content-start'>
                    <Col className="px-4" style={{width: '100%'}}>
                        <Row className='py-3'>
                            <Col className='text-size-36 main-header'>User Administration</Col>
                            <Col></Col>
                            <Col className="w-fc">
                                <Button
                                    block
                                    className='py-2 button-label'
                                    size='sm'
                                    style={{width: '118px'}}
                                    onClick={() => navigate('/admin-login')}
                                    color={Theme().colors.accentName}>Back to Admin Login
                                </Button>
                            </Col>
                        </Row>
                        <Row className="w-50">
                            <Label className='label-text-header form-label mb-2' for='manufacturer'>Manufacturer</Label>
                            <Select
                                captureMenuScroll={false}
                                classNamePrefix="custom-select"
                                filterOption={createFilter({ ignoreAccents: false })}
                                components={{ Option: CustomOption, MenuList: CustomMenuList }}
                                name='manufacturer'
                                placeholder='Select Manufacturer'
                                styles={selectStyles}
                                {...mfrSelectProps} />
                        </Row>
                        <Row className='pt-3 pb-2'>
                            <Col className='text-size-24 main-header'>Users</Col>
                        </Row>
                        <Row className='pb-3 w-100'>
                            <Col className="col-4 p-0" style={{marginLeft: '12px'}}>
                                <InputGroup size='sm'>
                                    <InputGroupText>
                                        <BsSearch/>
                                    </InputGroupText>
                                    <Input className='text-input' id="usersSearchInput" placeholder='Search...' onChange={(e) => filterUsers(e)}/>
                                </InputGroup>
                            </Col>
                            <Col></Col>
                            <Col className="w-fc p-0">
                                <Button
                                    block
                                    className='py-2 button-label'
                                    size='sm'
                                    style={{width: '118px'}}
                                    onClick={() => exportUsers()}
                                    color={Theme().colors.accentName}>Export Users
                                </Button>
                            </Col>
                            {mfrSelectProps.value &&
                            <Col className="w-fc ps-2">
                                <Button
                                    block
                                    className='py-2 button-label'
                                    size='sm'
                                    style={{width: '118px'}}
                                    onClick={() => inviteUser()}
                                    color={Theme().colors.accentName}>Invite User
                                </Button>
                            </Col>
                            }
                        </Row>
                        <Row className="mx-0" style={{height: '60vh', width: '96vw', border: '1px solid lightgray', background: 'white'}}>
                            <AutoSizer style={{padding: '0'}}>
                                {({width, height}) => (
                                    <Table className="table usersTable border-grayLight" id="usersTable"
                                        width={width - 2}
                                        height={height}
                                        headerHeight={34.5}
                                        rowHeight={38}
                                        rowCount={userList?.length}
                                        rowGetter={({ index }) => userList?.[index]?.value}
                                    >
                                        <Column
                                        label='Name'
                                        dataKey={'first_name'}
                                        width={width * 0.15}
                                        cellRenderer={({rowData}) => (<span>{rowData.first_name + ' ' + rowData.last_name}</span>)}
                                        />
                                        <Column
                                        label='Email'
                                        dataKey={'email'}
                                        width={width * 0.2}
                                        />
                                        <Column
                                        width={width * 0.2}
                                        label='Created Date'
                                        dataKey={'createdAt'}
                                        cellRenderer={({cellData}) => (<span>{new Date(cellData).toLocaleString()}</span>)}
                                        />
                                        <Column
                                        width={width * 0.2}
                                        label='Last Login Date'
                                        dataKey={'lastLoggedIn'}
                                        cellRenderer={({cellData}) => (<span>{new Date(cellData).toLocaleString()}</span>)}
                                        />
                                        <Column
                                        width={width * 0.1}
                                        label='Role'
                                        dataKey={'title'}
                                        />
                                        <Column
                                        width={width * 0.075}
                                        label=''
                                        dataKey={'title'}
                                        cellRenderer={({rowIndex}) => {
                                            const menuId = `user-menu-${rowIndex}`;
                                            const menuOptions = [
                                                {
                                                    label: 'Edit User',
                                                    onClick: () => {
                                                        editUser(userProps[rowIndex].value);
                                                        setOpenMenuIndex(null);
                                                    }
                                                },
                                                {
                                                    label: 'Login as User',
                                                    onClick: (e) => {
                                                        login(e, userProps[rowIndex]);
                                                        setOpenMenuIndex(null);
                                                    }
                                                },
                                                {
                                                    label: 'Send Invite Link',
                                                    onClick: () => {
                                                        sendInviteLink(userProps[rowIndex].value);
                                                        setOpenMenuIndex(null);
                                                    }
                                                }
                                            ];
                                            return (
                                                <div style={{justifyContent: 'end', display: 'flex'}}>
                                                    <Button 
                                                        id={menuId} 
                                                        onClick={() => toggleMenu(rowIndex)} 
                                                        className='py-1 button-label' 
                                                        size='sm' 
                                                        style={{width: '90px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'}}
                                                        color={Theme().colors.accentName}>
                                                        Actions <BsCaretDownFill size={12} />
                                                    </Button>
                                                    {openMenuIndex === rowIndex && (
                                                        <TooltipMenu 
                                                            target={menuId}
                                                            isOpen={true}
                                                            options={menuOptions}
                                                            toggle={() => setOpenMenuIndex(null)}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        }}
                                        />
                                    </Table>
                                )}
                            </AutoSizer>
                        </Row>
                    </Col>
                </Row>
            </Container>
            <EditUserSlideDrawer user={selectedUser} manufacturer={selectedMfr} adminLoginData={adminLoginData} allowDelete={true} refreshCb={getUsers}/>
            <ToastContainer
            position="bottom-center"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            />
        </Fragment>
    )
}

export default UserAdministration
